import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { getOmnivaLocations, initiateStripe, reset } from '../features/order/orderSlice';
import { clearCart } from '../features/product/productSlice';
import '../styling/checkout.css';
import Loading from '../components/Loading';
import OmnivaSelect from '../components/OmnivaSelect';
import UKAddressSelect from '../components/UKAddressSelect';
import { formatPrice as formatCurrency, getStoredMarket, resolveCurrency } from '../utils/currency';
import { analyticsItemFromCartItem, centsToAnalyticsAmount, trackEvent } from '../utils/analytics';

const MARKET_LABELS = {
  latvia: 'Latvia',
  uk: 'United Kingdom',
};

function Checkout() {
  const dispatch = useDispatch();
  const { cart, products } = useSelector((state) => state.product);
  const { omniva_locations, isLoading: orderLoading, isLocationsLoading, isError: orderError, message: orderMessage } = useSelector((state) => state.order);
  
  const storedMarket = getStoredMarket();
  const initialRegion = storedMarket === 'latvia' ? 'LV' : 'UK';

  // Form state
  const [clientDetails, setClientDetails] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postcode: '',
    country: initialRegion === 'LV' ? 'Latvia' : 'United Kingdom',
  });

  const [shippingMethod, setShippingMethod] = useState(initialRegion === 'LV' ? 'omniva' : 'standard');
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [shippingRegion, setShippingRegion] = useState(initialRegion);
  const [selectedOmniva, setSelectedOmniva] = useState(null);

  // Constants matching your backend
  const UK_SHIPPING_COST = 300; // 3.00 EUR in cents
  const OMNIVA_SHIPPING_COST = 300; // Adjust if Omniva differs

  // Calculate totals
  const itemPrice = cart.reduce((acc, item) => {
    const price = item.variant.discount_price || item.variant.price;
    return acc + (price * item.quantity);
  }, 0);

  const baseShippingCost =
    shippingRegion === 'LV' ? OMNIVA_SHIPPING_COST : UK_SHIPPING_COST;
  const shippingPrice = baseShippingCost;
  const totalPrice = itemPrice + shippingPrice;

  const formatPrice = (priceInCents) => formatCurrency(priceInCents, { market: storedMarket });

  const formatVariantOptions = (variant) => {
    if (!variant?.optionValues || variant.optionValues.length === 0) return '';
    return variant.optionValues
      .map((opt) => `${opt.name}: ${opt.value}`)
      .join(' • ');
  };

  const validateEmail = (email) => {
    if (typeof email !== 'string') return false;
    const trimmed = email.trim();
    if (!trimmed) return false;
    const emailRegex =
      /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i;
    return emailRegex.test(trimmed);
  };

  const validatePhoneNumber = (phone) => {
    if (typeof phone !== 'string') return false;
    const trimmed = phone.trim();
    if (!trimmed) return false;
    const normalized = trimmed.replace(/[\s().-]/g, '');
    if (!/^\+?\d+$/.test(normalized)) {
      return false;
    }
    const digitCount = normalized.replace(/^\+/, '').length;
    return digitCount >= 7 && digitCount <= 15;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setClientDetails(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRegionChange = (e) => {
    const region = e.target.value;
    setShippingRegion(region);
    setSelectedOmniva(null);
    setClientDetails((prev) => ({
      ...prev,
      address: '',
      city: '',
      postcode: '',
      country: region === 'LV' ? 'Latvia' : 'United Kingdom',
    }));
    setShippingMethod(region === 'LV' ? 'omniva' : 'standard');
  };

  const buildOmnivaAddress = (location) => {
    if (!location) {
      return {
        address: '',
        city: '',
        postcode: '',
      };
    }

    const street = [
      location.A5_NAME?.[0] || location.A5?.[0],
      location.A7_NAME?.[0] || location.A7?.[0],
    ]
      .filter(Boolean)
      .join(' ');

    const city =
      location.A3_NAME?.[0] ||
      location.A2_NAME?.[0] ||
      location.A3?.[0] ||
      location.A2?.[0] ||
      '';

    const address = [
      location.NAME?.[0],
      street,
    ].filter(Boolean).join(', ');

    return {
      address,
      city,
      postcode: location.ZIP?.[0] || '',
    };
  };

  const handleSelectOmniva = (location) => {
    setSelectedOmniva(location);
    const { address, city, postcode } = buildOmnivaAddress(location);
    setClientDetails((prev) => ({
      ...prev,
      address,
      city,
      postcode,
    }));
  };

  const validateForm = () => {
    if (!clientDetails.firstName || !clientDetails.lastName || !clientDetails.email) {
      toast.error('Please fill in all required fields');
      return false;
    }

    if (
      shippingRegion === 'UK' &&
      (!clientDetails.address || !clientDetails.city || !clientDetails.postcode)
    ) {
      toast.error('Please provide your full UK address');
      return false;
    }

    if (shippingRegion === 'LV' && !selectedOmniva) {
      toast.error('Please select an Omniva pickup location');
      return false;
    }

    if (!agreeTerms) {
      toast.error('Please agree to the terms and conditions');
      return false;
    }

    if (!validateEmail(clientDetails.email)) {
      toast.error('Please enter a valid email address');
      return false;
    }

    if (!validatePhoneNumber(clientDetails.phone)) {
      toast.error('Please enter a valid phone number');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    if (cart.length === 0) {
        toast.error('Your cart is empty');
        return;
    }

    const selectedMarket = (storedMarket || '').toLowerCase();
    if (!selectedMarket) {
        toast.error('Please select your market before checking out.');
        return;
    }

    const availableProductsById = new Set((products || []).map((product) => product._id));
    const unavailableItems = cart.filter((item) => {
        const itemMarkets = Array.isArray(item.countries)
            ? item.countries
                  .filter(Boolean)
                  .map((country) => (typeof country === 'string' ? country.toLowerCase() : country))
            : [];

        if (itemMarkets.length) {
            return !itemMarkets.includes(selectedMarket);
        }

        return selectedMarket ? !availableProductsById.has(item._id) : false;
    });

    if (unavailableItems.length) {
        const marketLabel = MARKET_LABELS[selectedMarket] || 'this market';
        toast.error(`Some items are not available in ${marketLabel}. Please review your cart.`);
        return;
    }

    setIsSubmitting(true);
    let redirected = false;

    try {
        const mergedDetails = {
            ...clientDetails,
            country: shippingRegion === 'LV' ? 'Latvia' : 'United Kingdom',
        };

        if (shippingRegion === 'LV' && selectedOmniva) {
            const { address, city, postcode } = buildOmnivaAddress(selectedOmniva);
            mergedDetails.address = address;
            mergedDetails.city = city;
            mergedDetails.postcode = postcode;
        }

        // Format order items to match your backend structure
        const orderItems = cart.map(item => ({
            _id: item._id,
            title: item.title,
            category: item.category,
            qty: item.quantity,
            variant: {
                _id: item.variant._id,
                price: item.variant.price,
                discount_price: item.variant.discount_price,
                variant_name: item.variant.variant_name || item.variant.optionValues?.map(opt => opt.value).join(', ') || 'Default',
                optionValues: item.variant.optionValues || [],
            }
        }));

        // Create order object matching your backend structure
        const orderData = {
            orderItems,
            clientDetails: mergedDetails,
            shipping_method: shippingMethod,
            shipping_region: shippingRegion,
            market: storedMarket,
            omniva_location: selectedOmniva ? {
                zip: selectedOmniva.ZIP?.[0] || null,
                name: selectedOmniva.NAME?.[0] || null,
                comment: selectedOmniva.COMMENT_LAV?.[0] || null,
                raw: selectedOmniva,
            } : null,
            agree_terms: agreeTerms,
            coupon: null // Add coupon support later if needed
        };

        const analyticsOrder = {
            currency: resolveCurrency({ market: storedMarket }).toUpperCase(),
            value: centsToAnalyticsAmount(totalPrice),
            shipping: centsToAnalyticsAmount(shippingPrice),
            shipping_tier: shippingMethod,
            items: cart.map(analyticsItemFromCartItem),
        };

        trackEvent('add_shipping_info', analyticsOrder);
        trackEvent('checkout_submit', analyticsOrder);

        console.log('Initiating Stripe payment with data:', orderData);

        // Dispatch the Stripe initiation
        const result = await dispatch(initiateStripe(orderData)).unwrap();
        
        if (result.url) {
            trackEvent('checkout_redirect', {
                ...analyticsOrder,
                payment_type: 'Stripe',
            });
            setIsRedirecting(true);
            redirected = true;
            dispatch(clearCart());
            localStorage.removeItem('cart');
            window.location.href = result.url;
        }

    } catch (error) {
        console.error('Order submission error:', error);
        toast.error(error || 'Failed to initiate payment');
    } finally {
        if (!redirected) {
            setIsSubmitting(false);
        }
    }
};

  // Add this useEffect to handle order errors
  useEffect(() => {
      if (orderError) {
          toast.error(orderMessage);
          dispatch(reset());
      }
  }, [orderError, orderMessage, dispatch]);

  useEffect(() => {
    if (shippingRegion === 'LV' && !omniva_locations.length) {
      dispatch(getOmnivaLocations());
    }
  }, [dispatch, shippingRegion, omniva_locations.length]);

  if (isRedirecting) {
    return (
      <div className="cart-page empty">
          <div className="empty-cart-card">
            <h2>Redirecting to Stripe…</h2>
            <p>Please wait while we take you to the secure payment page.</p>
          </div>
      </div>
    );
  }

  if (cart.length === 0 && !orderLoading) {
    return (
      <div className="cart-page empty">
          <div className="empty-cart-card">
            <h2>Your cart is empty</h2>
            <p>Add some products to continue to checkout.</p>
            <Link to="/shop" className="btn btn-primary m-2 no-hover-color">
              Browse products
            </Link>
          </div>
      </div>
    );
  }

  if (orderLoading) {
    return <Loading />;
  }

  return (
    <div className="checkout-page">
      <div className="container">
        <h1>Checkout</h1>

        <div className="checkout-content" style={{ display: 'grid', gridTemplateColumns: '1fr 400px', gap: '2rem' }}>
          {/* Left Column - Forms */}
          <div className="checkout-forms">
            <form onSubmit={handleSubmit}>
              {/* Customer Information */}
              <div className="form-section">
                <h3>Customer Information</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div className="form-group">
                    <label>First Name *</label>
                    <input
                      type="text"
                      name="firstName"
                      className="form-input"
                      value={clientDetails.firstName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Last Name *</label>
                    <input
                      type="text"
                      name="lastName"
                      className="form-input"
                      value={clientDetails.lastName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    className="form-input"
                    value={clientDetails.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Phone Number</label>
                  <input
                    type="tel"
                    name="phone"
                    className="form-input"
                    value={clientDetails.phone}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              {/* Shipping Information */}
              <div className="form-section">
                <h3>Shipping Information</h3>
                <div className="form-group">
                  <label>Shipping Country *</label>
                  <select
                    name="country"
                    className="form-input"
                    value={shippingRegion}
                    onChange={handleRegionChange}
                    disabled={true}
                  >
                    <option value="UK">United Kingdom</option>
                    <option value="LV">Latvia (Omniva pickup)</option>
                  </select>
                </div>

                {shippingRegion === 'LV' ? (
                  <>
                    <OmnivaSelect
                      locations={omniva_locations}
                      onSelect={handleSelectOmniva}
                      selectedLocation={selectedOmniva}
                      isLoading={isLocationsLoading}
                    />
                    {selectedOmniva && (
                      <div className="omniva-selection-summary">
                        <p><strong>Selected pickup:</strong> {selectedOmniva.NAME?.[0]}</p>
                        <p>
                          {[
                            selectedOmniva.A5_NAME?.[0] || selectedOmniva.A5?.[0],
                            selectedOmniva.A7_NAME?.[0] || selectedOmniva.A7?.[0],
                          ]
                            .filter(Boolean)
                            .join(' ') || '—'}
                        </p>
                        <p>
                          {[
                            selectedOmniva.A3_NAME?.[0] || selectedOmniva.A3?.[0],
                            selectedOmniva.A2_NAME?.[0] || selectedOmniva.A2?.[0],
                          ]
                            .filter(Boolean)
                            .join(', ') || '—'}
                        </p>
                        {selectedOmniva.COMMENT_LAV?.[0] && (
                          <p className="omniva-selection-note">
                            {selectedOmniva.COMMENT_LAV[0]}
                          </p>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <UKAddressSelect
                    address={clientDetails.address}
                    city={clientDetails.city}
                    postcode={clientDetails.postcode}
                    onFieldChange={handleInputChange}
                    onAddressAutoFill={(fields) =>
                      setClientDetails((prev) => ({
                        ...prev,
                        ...fields,
                      }))
                    }
                  />
                )}
              </div>


              {/* Terms and Conditions */}
              <div className="form-section">
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={agreeTerms}
                      onChange={(e) => setAgreeTerms(e.target.checked)}
                      style={{ marginRight: '8px' }}
                      required
                    />
                    I agree to the terms and conditions *
                  </label>
                </div>
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
                style={{ width: '100%', padding: '12px', fontSize: '1.1em', marginTop: '1rem' }}
              >
                {isSubmitting ? 'Processing...' : `Place Order - ${formatPrice(totalPrice)}`}
              </button>
            </form>
          </div>

          {/* Right Column - Order Summary */}
          <div className="order-summary">
            <div style={{ border: '1px solid var(--color-border)', borderRadius: '8px', padding: '1.5rem', background: 'var(--color-canvas)' }}>
              <h3>Order Summary</h3>
              <div className="summary-line" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem', fontSize: '0.95em' }}>
                <span>Shipping method</span>
                <span>{shippingRegion === 'LV' ? 'Omniva pickup (Latvia)' : 'Standard shipping (UK)'}</span>
              </div>
              {shippingRegion === 'LV' && selectedOmniva && (
                <div style={{ marginBottom: '1rem', fontSize: '0.9em', color: 'rgba(0, 86, 112, 0.7)' }}>
                  <div style={{ fontWeight: 600 }}>{selectedOmniva.NAME?.[0]}</div>
                  <div>
                    {[
                      selectedOmniva.A5_NAME?.[0] || selectedOmniva.A5?.[0],
                      selectedOmniva.A7_NAME?.[0] || selectedOmniva.A7?.[0],
                    ]
                      .filter(Boolean)
                      .join(' ') || '—'}
                  </div>
                  <div>
                    {[
                      selectedOmniva.A3_NAME?.[0] || selectedOmniva.A3?.[0],
                      selectedOmniva.A2_NAME?.[0] || selectedOmniva.A2?.[0],
                    ]
                      .filter(Boolean)
                      .join(', ') || '—'} {selectedOmniva.ZIP?.[0] ? `LV-${selectedOmniva.ZIP[0]}` : ''}
                  </div>
                </div>
              )}
              
              <div className="order-items" style={{ marginBottom: '1rem' }}>
                {cart.map((item) => {
                  const itemPrice = item.variant.discount_price || item.variant.price;
                  return (
                    <div key={`${item._id}-${item.variant._id}`} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem', fontSize: '0.9em' }}>
                      <span style={{ display: 'flex', flexDirection: 'column' }}>
                        <strong>{item.title}</strong>
                        {formatVariantOptions(item.variant) && (
                          <span style={{ color: 'var(--color-ink-soft)', fontSize: '0.85em' }}>
                            {formatVariantOptions(item.variant)}
                          </span>
                        )}
                        <span style={{ color: 'var(--color-ink-soft)' }}>Qty: {item.quantity}</span>
                      </span>
                      <span>{formatPrice(itemPrice * item.quantity)}</span>
                    </div>
                  );
                })}
              </div>

              <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span>Subtotal:</span>
                  <span>{formatPrice(itemPrice)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span>Shipping:</span>
                  <span>{formatPrice(shippingPrice)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1.2em', fontWeight: 'bold', borderTop: '1px solid var(--color-border)', paddingTop: '0.5rem' }}>
                  <span>Total:</span>
                  <span>{formatPrice(totalPrice)}</span>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Checkout;
